import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Badge,
  Switch,
  FormControlLabel,
  IconButton,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Logout, Notifications, Add, Settings, ChevronLeft, ChevronRight, AdminPanelSettings, CalendarToday, Flag } from '@mui/icons-material';
import SvgIcon from '@mui/material/SvgIcon';
import Icon from './Icon';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useLogoutBlocker } from '../contexts/LogoutBlockerContext';
import Avatar from './Avatar';
import NotificationsMenu from './NotificationsMenu';
import FloatingMenu from './FloatingMenu';
import { clearClientCache } from '../utils/clearClientCache';

const NightModeIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 20 20">
    <path
      d="M19.0819 12.1954C18.4763 14.1726 17.2624 15.9083 15.6132 17.1557C14.1652 18.2455 12.4421 18.9102 10.6373 19.0751C8.83256 19.24 7.01754 18.8987 5.396 18.0894C3.77446 17.2801 2.41057 16.0348 1.45743 14.4934C0.504283 12.952 -0.000393014 11.1755 4.58827e-05 9.36321C-0.00647008 7.24789 0.681196 5.18884 1.95755 3.50196C3.20493 1.8527 4.9407 0.638878 6.91786 0.0332109C7.04817 -0.00691387 7.18695 -0.0107561 7.31928 0.0220974C7.45162 0.0549509 7.57249 0.123257 7.6689 0.21967C7.76531 0.316083 7.83362 0.436954 7.86647 0.569286C7.89933 0.701617 7.89548 0.8404 7.85536 0.970711C7.42302 2.4008 7.38677 3.92139 7.75047 5.37046C8.11417 6.81952 8.8642 8.14277 9.92062 9.1992C10.977 10.2556 12.3003 11.0056 13.7494 11.3693C15.1984 11.733 16.719 11.6968 18.1491 11.2645C18.2794 11.2243 18.4182 11.2205 18.5505 11.2533C18.6829 11.2862 18.8037 11.3545 18.9002 11.4509C18.9966 11.5473 19.0649 11.6682 19.0977 11.8005C19.1306 11.9329 19.1267 12.0716 19.0866 12.202L19.0819 12.1954Z"
      fill="currentColor"
    />
  </SvgIcon>
);

const FixedSideNav = ({ onWidthChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(CONTEXTS.AuthContext);
  const queryClient = useQueryClient();
  const api = useContext(CONTEXTS.APIContext);
  const { openLogin } = useAuthModal();
  const { runLogout } = useLogoutBlocker();
  const { totalUnreadCount } = CONTEXTS.useMessages();
  const { unreadCount } = CONTEXTS.useNotifications();
  const { isDarkMode, toggleTheme } = CONTEXTS.useTheme();

  // Responsive: Hide sidebar on mobile and tablet (< 900px)
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [shelfTotals, setShelfTotals] = useState({ owned: 0, ordered: 0, wished: 0 });
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [floatingMenuAnchorEl, setFloatingMenuAnchorEl] = useState(null);
  
  // Get initial collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const username = (currentUser?.username && currentUser?.username !== '@')
  ? currentUser.username
  : (() => {
      const cached = localStorage.getItem('currentUser');
      if (cached) {
        const prev = JSON.parse(cached);
        return prev.username && prev.username !== '@' ? prev.username : 'me';
      }
      return 'me';
    })();

    // Refresh user data when app comes back online
    useEffect(() => {
      const handleOnline = async () => {
        if (api && currentUser?.id) {
          try {
            const userController = new CONTROLLERS.UserController(api);
            const res = await userController.getProfile(currentUser.id);
            const user = res?.data?.data || res?.data || res;
            if (user) {
              // Fallback for username
              if (!user.username || user.username === '@') {
                user.username = username;
              }
              // Fallback for avatar
              if (!user.profile?.currentAvatar?.url && currentUser?.profile?.currentAvatar?.url) {
                user.profile = user.profile || {};
                user.profile.currentAvatar = currentUser.profile.currentAvatar;
              }
              setCurrentUser(user);
              localStorage.setItem('currentUser', JSON.stringify(user));
            }
          } catch (err) {
            // Optionally handle error
          }
        }
      };
      window.addEventListener('online', handleOnline);
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }, [api, currentUser?.id]);
  const isGuest = !auth?.isSignedIn;
  
  // Navigation menu items (below user profile)
  const isAdmin = !!(auth?.userModel?.adminLevel);

  const menuItems = [
    { id: 'database', icon: 'database', label: 'Database', path: '/database' },
    ...(isGuest ? [] : [{ id: 'publicMarket', icon: 'marketplace', label: 'Public Market', path: '/marketplace' }]),
    ...(isGuest ? [] : [{ id: 'groups', icon: 'groups', label: 'Groups', path: '/groups' }]),
    ...(isGuest ? [] : [{ id: 'messages', icon: 'messages', label: 'Messages', path: '/messages' }]),
    // { id: 'profile', icon: 'profile', label: 'Profile', path: isGuest ? '/login' : `/user/${username}` },
    { id: 'wiki', icon: 'wiki', label: 'Shelf-Care Wiki', path: '/shelf-wiki' },
    ...(isGuest ? [] : [{ id: 'ranking', icon: 'ranking', label: 'Ranking', path: '/ranking' }]),
    ...(isAdmin ? [{ id: 'adminUsers', icon: null, muiIcon: AdminPanelSettings, label: 'Admin Users', path: '/admin/users' }] : []),
    ...(isAdmin ? [{ id: 'adminReports', icon: null, muiIcon: Flag, label: 'Admin Reports', path: '/admin/reports' }] : []),
  ];

   useEffect(() => {
    // Get current user from auth context
    if (auth.userModel || auth.user) {
      const user = auth.userModel || auth.user;
      setCurrentUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
    }
  }, [auth.user, auth.userModel]);

  // Preload avatar image to reduce loading time
  const EXTRA_TOP = 0; // additional pixels below the search input (0 = align exactly)
  const BOTTOM_GAP = 10; // space from viewport bottom

  useEffect(() => {
    if (currentUser?.profile?.currentAvatar?.url || currentUser?.currentAvatar?.url) {
      const img = new Image();
      img.src = currentUser?.profile?.currentAvatar?.url || currentUser?.currentAvatar?.url;
    }
  }, [currentUser]);

  useEffect(() => {
    let mounted = true;

    if (isGuest || !currentUser?.id) {
      setShelfTotals({ owned: 0, ordered: 0, wished: 0 });
      return () => {
        mounted = false;
      };
    }

    const shelfController = new CONTROLLERS.ShelfController(api);

    (async () => {
      try {
        const res = await shelfController.getAllCollections('shelf');
        const collections = res?.data?.data || res?.data || res || { owned: [], ordered: [], wished: [] };

        const owned = Array.isArray(collections?.owned) ? collections.owned.length : 0;
        const ordered = Array.isArray(collections?.ordered) ? collections.ordered.length : 0;
        const wished = Array.isArray(collections?.wished) ? collections.wished.length : 0;

        if (!mounted) return;
        setShelfTotals({ owned, ordered, wished });
      } catch (e) {
        if (!mounted) return;
        setShelfTotals({ owned: 0, ordered: 0, wished: 0 });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [api, currentUser?.id, isGuest]);

  useEffect(() => {
    if (isGuest) return;
    // Find messages item index (it's at index 3: database=0, publicMarket=1, groups=2, messages=3)
    const messagesIndex = menuItems.findIndex(item => item.id === 'messages');
    if (messagesIndex !== -1) {
      if (totalUnreadCount > 0) {
        menuItems[messagesIndex].badge = totalUnreadCount;
      } else {
        delete menuItems[messagesIndex].badge;
      }
    }
  }, [totalUnreadCount, isGuest])

  const handleNavigation = (path) => {
    if (isGuest && path === '/login') {
      openLogin();
      return;
    }
    navigate(path);
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleFloatingMenuOpen = (event) => {
    setFloatingMenuAnchorEl(event.currentTarget);
  };

  const handleFloatingMenuClose = () => {
    setFloatingMenuAnchorEl(null);
  };

  const handleFloatingMenuOptionPress = (option) => {
    switch (option.id) {
      case 'post':
        navigate('/', { state: { openPostCreator: true } });
        break;
      case 'figure':
        navigate('/create-figure');
        break;
      case 'company':
        if (window.location.pathname === '/') {
          navigate('/', { state: { openCompanyModal: true } });
        } else {
          navigate('/create-company');
        }
        break;
      case 'group':
        if (window.location.pathname === '/') {
          navigate('/', { state: { openGroupModal: true } });
        } else {
          navigate('/create-group');
        }
        break;
      default:
        break;
    }
  };

  const handleLogout = async () => {
    navigate('/', { replace: true });
    await runLogout(async () => {
      await clearClientCache({ queryClient });
      await auth.signOut();
      setCurrentUser(null);
      localStorage.removeItem('currentUser');
    });
  };

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
    // Notify parent of width change (width + left offset)
    if (onWidthChange) {
      onWidthChange(newCollapsed ? 90 : 256); // 74+16 or 240+16
    }
  };

  // Custom three-line icon component
  const ThreeLineIcon = ({ collapsed }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: 20,
        height: 16,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 2,
          backgroundColor: 'currentColor',
          borderRadius: 0.5,
        }}
      />
      <Box
        sx={{
          width: collapsed ? '60%' : '100%',
          height: 2,
          backgroundColor: 'currentColor',
          borderRadius: 0.5,
          alignSelf: 'flex-start',
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: 2,
          backgroundColor: 'currentColor',
          borderRadius: 0.5,
        }}
      />
    </Box>
  );

  // Hide sidebar on settings pages (they have their own layout)
  const hiddenPaths = ['/settings', '/profile/edit', '/profile/views', '/profile/details', '/profile-setup', '/activity', '/privacy-policy'];
  const isHiddenPath = hiddenPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  // Notify parent of initial width, and reset when hidden
  useEffect(() => {
    if (isMobileOrTablet || isHiddenPath) {
      if (onWidthChange) onWidthChange(0);
      setFloatingMenuAnchorEl(null);
    } else {
      if (onWidthChange) onWidthChange(isCollapsed ? 90 : 256);
    }
  }, [isMobileOrTablet, isHiddenPath, isCollapsed]);

  // Hide sidebar on mobile/tablet or settings pages
  if (isMobileOrTablet || isHiddenPath) {
    return null;
  }

  const sidebarWidth = isCollapsed ? 74 : 240;
  const topOffset = 25; // Fixed position, not calculated
  const toggleRightOffset = 16; // gap from right edge
  const toggleGap = 8; // gap between sidebar and toggle
  const toggleTopGap = 6; // distance below topOffset for toggle

  return (
    <Box
      sx={{
        width: sidebarWidth,
        position: 'fixed',
        left: 16,
        top: `${topOffset + EXTRA_TOP}px`,
        bottom: `${BOTTOM_GAP}px`,
        backgroundColor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRight: 'none',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isCollapsed ? 'center' : 'stretch',
        zIndex: 9998,
        overflowY: 'auto',
        transition: 'width 0.3s ease, left 0.3s ease, top 0.2s ease',
        boxShadow: (theme) => theme.shadows?.[4] || '0 8px 24px rgba(0,0,0,0.12)',
        // Hide scrollbar completely
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari
        },
      }}
    >
      {/* Logo and Brand */}
      <Box sx={{ px: isCollapsed ? 0 : 1, mb: 0 }}>
        <Box 
          onClick={() => navigate('/')}
          sx={{
            mb: 2, 
            p: isCollapsed ? 1.5 : 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            overflow: 'hidden',
            position: 'relative',
            cursor: 'pointer',
          }}
        >
          {!isCollapsed && (
            <Box
              component="img"
              src="/assets/logos/logo_1.png"
              alt="FigShelf"
              sx={{
                width: '80%',
                transition: 'opacity 0.2s ease',
              }}
            />
          )}
          {isCollapsed && (
            <Box
              sx={{
                width: 40,
                height: 40,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box
                component="img"
                src="/assets/logos/logo_1.png"
                alt="FigShelf"
                sx={{
                  width: 160, // 4x the container width to show 1/4
                  height: 40,
                  objectFit: 'cover',
                  objectPosition: 'left center',
                  transition: 'opacity 0.2s ease',
                }}
              />
            </Box>
          )}
        </Box>
   
      {/* Home Button */}
      <Box sx={{ px: 1, mb: 1 }}>
        <ListItemButton
          onClick={() => handleNavigation('/')}
          sx={{
            borderRadius: 2,
            backgroundColor: (theme) => isActive('/') ? theme.palette.primary.main : 'transparent',
            color: (theme) => isActive('/') ? 'white' : '#5E7F83',
            '&:hover': {
              backgroundColor: (theme) => isActive('/') ? theme.palette.primary.dark : theme.palette.action.hover,
            },
            py: 1.5,
            px: isCollapsed ? 1 : 1.5,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: isCollapsed ? 0 : 40, justifyContent: 'center' }}>
            <Icon name="home" color={isActive('/') ? 'white' : '#5E7F83'} />
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary="Home"
              primaryTypographyProps={{
                fontWeight: isActive('/') ? 'bold' : 'normal',
                fontSize: '0.9rem',
              }}
            />
          )}
        </ListItemButton>
      </Box>
      </Box>

      {currentUser ? (
        <Box sx={{ 
          px: 1, 
          mb: isCollapsed ? 1.5 : 2,
        }}>
        {!isCollapsed && (
          <Box  onClick={() => navigate(`/user/${currentUser?.username}`)} 
            sx={{
            borderRadius: 2,
            cursor: 'pointer',
            backgroundColor: isActive(`/user/${currentUser?.username}`) ? 'action.selected' : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}

          >
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, p: 2, alignItems: 'center', overflow: 'hidden' }}>
              <Box sx={{ width: 60, height: 60, borderRadius: 1, flexShrink: 0 }}>
                <Avatar src={currentUser?.profile?.currentAvatar?.url || currentUser?.currentAvatar?.url || null} fullWidth={true} variant={'square'} size={60} sx={{borderRadius: 1}} isPremium={!!currentUser?.isPremium}/>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body1" fontWeight={800} fontSize={14} color="text.primary" noWrap>
                  {currentUser?.profile?.displayName || currentUser?.username || 'User'}
                </Typography>
                <Typography variant="body2" fontSize={10} color="text.secondary" noWrap>
                  @{currentUser?.username}
                </Typography>
              </Box>
            </Box>
        </Box>
        )}
        {isCollapsed && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: 'hidden', display: 'inline-block' }}>
              <Avatar
                onClick={() => navigate(`/user/${currentUser?.username}`)}
                src={currentUser?.profile?.currentAvatar?.url || currentUser?.currentAvatar?.url || null}
                variant="square"
                sx={{ width: '100%', height: '100%', borderRadius: 1, cursor: 'pointer' }}
                isPremium={!!currentUser?.isPremium}
              />
            </Box>
          </Box>
        )}

        {/* Statistics Section */}
        {!isCollapsed && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 1, alignItems: 'center' }}>
                <Box sx={{ textAlign: 'center',  }}>
                  <Typography variant="h6" fontWeight="bold" color="#000">
                    {shelfTotals.owned.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Owned
                  </Typography>
                </Box>
                <Divider orientation="vertical" sx={{ color: '#000', height: 40 }}/>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="#000">
                    {shelfTotals.ordered.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ordered
                  </Typography>
                </Box>
                <Divider orientation="vertical" sx={{ color: '#000', height: 40 }}/>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="#000">
                    {shelfTotals.wished.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Wished
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Thin vertical divider */}
            <Divider sx={{ my: 1 }} />

            {/* Level Progress Section */}
            <Box sx={{ px: 2, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                  Level 1
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LinearProgress
                    variant="determinate"
                    value={45}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: '#49A0AD',
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  50 point to level 2
                </Typography>
              </Box>
            </Box>

            {/* Thin vertical divider */}
            <Divider sx={{ my: 1 }} />

            {/* Achievements Section */}
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2">
                <span style={{ fontWeight: 'normal' }}>Achievements </span>
                <span style={{ fontWeight: 'bold' }}>Level 1 (210 points)</span>
              </Typography>
            </Box>

            {/* Thin vertical divider */}
            <Divider sx={{ my: 1 }} />
          </>
        )}
        </Box>
      ) : null}

      {/* Navigation Menu Items */}
      <Box sx={{ px: 1, flex: 1 }}>
        <List disablePadding>
          {menuItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5, position: 'relative', zIndex: 1002 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  backgroundColor: (theme) => isActive(item.path) ? '#49A0AD' : 'transparent',
                  color: (theme) => isActive(item.path) ? '#fff' : '#5E7F83',
                  '&:hover': {
                    backgroundColor: (theme) => isActive(item.path) ? '#3d8a96' : theme.palette.action.hover,
                  },
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  position: 'relative',
                  zIndex: 1002,
                }}
                title={isCollapsed ? item.label : ''}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: isCollapsed ? 0 : 40, justifyContent: 'center' }}>
                  {item.muiIcon
                    ? <item.muiIcon sx={{ color: isActive(item.path) ? '#fff' : '#5E7F83', fontSize: 22 }} />
                    : <Icon name={item.icon} color={isActive(item.path) ? '#fff' : '#5E7F83'} />}
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isActive(item.path) ? 'bold' : 'normal',
                      }}
                    />
                    {item.badge && (
                      <Badge
                        badgeContent={item.badge}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.7rem',
                            height: 18,
                            minWidth: 18,
                          },
                        }}
                      />
                    )}
                  </>
                )}
                {isCollapsed && item.badge && (
                  <Badge
                    badgeContent={item.badge}
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        height: 18,
                        minWidth: 18,
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Toggle Button - Positioned in the middle */}
      <IconButton
        onClick={toggleCollapse}
        size="small"
        sx={{
          color: 'text.secondary',
          position: 'fixed',
          left: isCollapsed ? 68 : 250,
          top: '50vh',
          transform: 'translateY(-50%)',
          width: 32,
          height: 32,
          zIndex: 1001,
          opacity: 0.3,
          transition: 'all 0.2s ease',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '50%',
          pointerEvents: 'auto',
          '&:hover': {
            opacity: 1,
            backgroundColor: 'action.hover',
            transform: 'translateY(-50%) scale(1.15)',
            color: 'text.primary',
          },
        }}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
      </IconButton>

      {/* Bottom Controls */}
     {
      !isGuest && (
         <Box sx={{ p: isCollapsed ? 1 : 2, borderTop: (theme) => `1px solid ${theme.palette.divider}`, position: 'relative', zIndex: 10000 }}>
        <List sx={{ px: isCollapsed ? 0 : 1 }}>
          {!isCollapsed && (
            <>
              {/* Settings */}
              <ListItem disablePadding sx={{ mb: 1, position: 'relative', zIndex: 10000 }}>
                <ListItemButton
                  onClick={handleSettings}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    color: '#5E7F83',
                    position: 'relative',
                    zIndex: 10000,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 25 }}>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText
                    primary="Settings"
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: 'normal',
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Dark Mode Toggle after Logout */}
              <ListItem disablePadding sx={{ position: 'relative', zIndex: 10000 }}>
                <ListItemButton
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 10000,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 25, color: '#51A2B5' }}>
                    <NightModeIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dark mode"
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      noWrap: true,
                    }}
                    sx={{
                      m: 0,
                      '& .MuiListItemText-primary': {
                        color: '#51A2B5',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                  <Switch
                    size="small"
                    checked={isDarkMode}
                    onChange={toggleTheme}
                    sx={{ ml: 1, flexShrink: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}

          <Box sx={{ 
            display: 'flex', 
            justifyContent: isCollapsed ? 'center' : 'space-between', 
            alignItems: 'center', 
            mt: 1, 
            px: isCollapsed ? 0 : 0.5,
            flexDirection: isCollapsed ? 'column' : 'row',
            gap: isCollapsed ? 1 : 0,
            position: 'relative',
            zIndex: 9999,
          }}>
            {isCollapsed && (
              <>
                <IconButton
                  size="small"
                  onClick={handleSettings}
                  sx={{
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                    color: '#5E7F83',
                    position: 'relative',
                    zIndex: 10000,
                  }}
                  title="Settings"
                >
                  <Settings fontSize="small" />
                </IconButton>
                
                <IconButton
                  size="small"
                  onClick={toggleTheme}
                  sx={{
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                    color: '#51A2B5',
                    position: 'relative',
                    zIndex: 10000,
                  }}
                  title={isDarkMode ? 'Light mode' : 'Dark mode'}
                >
                  <NightModeIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </>
            )}

            <IconButton
              size="small"
              onClick={handleNotificationMenuOpen}
              sx={{
                borderRadius: 2,
                width: 40,
                height: 40,
                position: 'relative',
                zIndex: 10000,
              }}
              title="Notifications"
            >
              <Badge
                badgeContent={unreadCount || 0}
                color="error"
                invisible={!unreadCount}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    height: 16,
                    minWidth: 16,
                  },
                }}
              >
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>

            <IconButton
              size="small"
              onClick={handleFloatingMenuOpen}
              sx={{
                borderRadius: 2,
                width: 40,
                height: 40,
                position: 'relative',
                zIndex: 10000,
              }}
              title="Add"
            >
              <Add fontSize="small" />
            </IconButton>
          </Box>
        </List>

        <NotificationsMenu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
        />

        <FloatingMenu
          anchorEl={floatingMenuAnchorEl}
          open={Boolean(floatingMenuAnchorEl)}
          onClose={handleFloatingMenuClose}
          onOptionPress={handleFloatingMenuOptionPress}
        />
      </Box>
      )
     }
    </Box>
  );
};

export default FixedSideNav;
